import type { MiniAction } from '@otto-ec/tracking-bct';
import { getPageCluster } from '@shared/domain/PageCluster.ts';
import { log } from '@shared/log.browser.ts';
import defaultTrackingGateway from '@shared/tracking/Tracking.browser';
import type { TrackingGateway } from '@shared/tracking/Tracking.browser';
import type { FrontendTrackingLabels, KnownTrackingLabels, TrackingLabels } from '@shared/tracking/TrackingLabels';
import { isSimilarProduct } from '@shared/types/types';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { BlackFridaySliderItem, ProductItem, TileItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import {
    featureIdFromLocalListPosition,
    featuresFromTileListItems,
} from '@tilelist/domain/tracking/FeatureTracking.ts';
import { listFeaturesLabels } from '@tilelist/domain/tracking/SliderTracking.ts';
import { BlackFridaySliderTracking } from '@tilelist/services/tracking/BlackFridaySliderTracking.browser.ts';
import { getSelectionRulesTracking } from '@tilelist/services/tracking/SelectionRuleTracking.browser.ts';

export class TrackingService implements TrackingPort {
    private readonly blackFridaySlider: BlackFridaySliderTracking;

    constructor(private readonly gateway: TrackingGateway = defaultTrackingGateway) {
        this.blackFridaySlider = new BlackFridaySliderTracking(gateway);
    }

    async getPageViewTrackingSfid(): Promise<string> {
        return this.gateway.getPageMergeId();
    }

    async replaceContext(contextId: string, url: string): Promise<void> {
        log.debug(`replaceContext -- contextId: ${contextId} -- url: ${url}`);
        await this.gateway.replaceContext(contextId, url);
    }

    trackTileVisibility(item: TileItem): void {
        const featureId = featureIdFromLocalListPosition(item.localListPosition);
        const miniAction: MiniAction = { featureId, action: 'scroll', status: 'visible' };

        log.debug(`trackTileVisibility -- miniAction ${JSON.stringify(miniAction)}`);
        this.gateway.submitMiniActions([miniAction]);
    }

    trackClick(event: Event, productItem: ProductItem, rule: string, entryRule: string): void {
        const trackingData = this.productClickLabels(event, productItem, rule, entryRule);
        if (!trackingData) return;

        log.debug(`trackClick ${JSON.stringify(trackingData)}`);
        this.gateway.trackOnNextPageImpression(trackingData);
    }

    trackBlackFridaySliderLoaded(slider: BlackFridaySliderItem): void {
        this.blackFridaySlider.loaded(slider);
    }

    trackBlackFridaySliderVisible(tiles: ProductItem[]): void {
        this.blackFridaySlider.visible(tiles);
    }

    trackBlackFridaySliderBrowsed(tiles: ProductItem[]): void {
        this.blackFridaySlider.browsed(tiles);
    }

    trackBlackFridaySliderClick(event: Event, productItem: ProductItem, rule: string, entryRule: string): void {
        this.blackFridaySlider.clicked();

        const trackingData = this.productClickLabels(event, productItem, rule, entryRule);
        if (!trackingData) return;

        trackingData.san_Interaction = 'black_friday_slider_product_tile';
        trackingData['wk.san_ProductType'] = `slider_${productItem.product.productType}`;
        trackingData['wk.san_ItemSource'] = 'blackFridaySlider';
        trackingData['wk.san_ItemPosition'] = productItem.localListPosition;
        this.gateway.trackOnNextPageImpression(trackingData);
    }

    private productClickLabels(
        event: Event,
        productItem: ProductItem,
        rule: string,
        entryRule: string,
    ): TrackingLabels | undefined {
        const { currentTarget } = event;
        if (!(currentTarget instanceof HTMLElement)) return undefined;

        const element = currentTarget;
        const linkType = element.dataset.linkType;
        const pageCluster = getPageCluster();
        if (!linkType || !pageCluster) return undefined;

        const trackingData: TrackingLabels = productItem.product.clickTracking
            ? {
                  ...productItem.product.clickTracking,
                  ...getSelectionRulesTracking(rule, entryRule),
              }
            : getSelectionRulesTracking(rule, entryRule);

        if (isSimilarProduct(productItem.product)) {
            trackingData['wk.san_ListPositionSimilar'] = productItem.originPosition + 1;
        }

        trackingData.san_Interaction = `${productItem.product.productType}_product_tile`;
        trackingData['wk.san_ListPosition'] = productItem.actualListPosition;
        trackingData['wk.san_ProductType'] = productItem.product.productType;
        trackingData['wk.san_ProductClickListType'] = pageCluster;
        trackingData['wk.san_ProductLinkType'] = linkType;

        return trackingData;
    }

    trackPageView(pageView: TileListPageLayout, frontendTracking: FrontendTrackingLabels): void {
        this.blackFridaySlider.reset();
        const baseTrackingLabels = {
            ...frontendTracking,
            ...listFeaturesLabels(pageView.tileListItems),
            ...pageView.tracking,
        };

        const features = featuresFromTileListItems(pageView.tileListItems);

        log.debug(`trackOnPageView ${JSON.stringify(baseTrackingLabels)} -- ${JSON.stringify(features)}`);
        this.gateway.addToPageImpression(baseTrackingLabels || {}, features || []);
    }

    trackPagination() {
        log.debug(`trackPagination -- san_Interaction: paging_bottom`);
        this.gateway.addToPageImpression({ san_Interaction: 'paging_bottom' });
    }

    trackUrl(url: string): void {
        log.debug(`trackUrl ${url}`);
        this.gateway.addToPageImpression({
            ot_VisibleUrl: decodeURI(url),
        });
    }

    trackPageCluster(pageCluster: string) {
        log.debug(`trackPageCluster ${pageCluster}`);
        this.gateway.addToPageImpression({ ot_PageCluster: pageCluster });
    }

    trackComparisonActivity(activity: NonNullable<KnownTrackingLabels['san_ComparisonActivity']>) {
        log.debug(`trackComparisonActivity -- activity: ${activity}`);
        this.gateway.submitEvent({ san_ComparisonActivity: activity });
    }

    trackInteraction(interaction: KnownTrackingLabels['san_Interaction']) {
        log.debug(`trackInteraction -- san_Interaction: ${interaction}`);
        this.gateway.submitEvent({ san_Interaction: interaction });
    }

    trackProductImageChange(imagePosition: number) {
        const flexLabel = `image_${imagePosition}`;
        log.debug(
            `trackProductImageChange -- san_Interaction: product_image_change -- san_ReptileExperimentFlexLabel: ${flexLabel}`,
        );
        this.gateway.submitEvent({
            san_Interaction: 'product_image_change',
            san_ReptileExperimentFlexLabel: flexLabel,
        });
    }

    trackListLayer(layer: NonNullable<KnownTrackingLabels['san_ListLayer']>) {
        log.debug(`trackListLayer -- san_ListLayer: ${layer}`);
        this.gateway.submitEvent({ san_ListLayer: layer });
    }
}
